/**
 * Deploy an agent (stage 2b): the Workshop dialog with its pegs. The agents
 * are a list inside the dialog (Find narrows it), so the list can never open
 * wider than the dialog (the old picker's popover opened 1,428px wide from
 * the page edge). Pick one, give it an objective, and it runs once.
 */
import { useMemo, useRef, useState } from "react";
import { Check, Search } from "lucide-react";
import { cn } from "@/utils/helpers";
import { useWorkshopAgents } from "../api/useWorkshopDeployments";
import WorkshopDialog from "./WorkshopDialog";
import { WsInlineError } from "./WorkshopParts";

const OBJECTIVE_MIN_LENGTH = 10;

export type DeployAgentVars = {
  definitionId: string;
  name?: string;
  objective: string;
};

type Props = {
  onClose: () => void;
  /** done(error?): close on success, surface the message on failure. */
  onSubmit: (vars: DeployAgentVars, done: (error?: string) => void) => void;
};

export default function DeployAgentModal({ onClose, onSubmit }: Props) {
  const agentsQuery = useWorkshopAgents();
  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);
  const [find, setFind] = useState("");
  const [definitionId, setDefinitionId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [objective, setObjective] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const q = find.trim().toLowerCase();
  const shown = q
    ? agents.filter(
        (a) => a.name.toLowerCase().includes(q) || (a.description ?? "").toLowerCase().includes(q),
      )
    : agents;
  const selectedAgent = agents.find((a) => a.id === definitionId) ?? null;
  const trimmedObjective = objective.trim();
  const canSubmit =
    !!definitionId &&
    !!selectedAgent?.enabled &&
    trimmedObjective.length >= OBJECTIVE_MIN_LENGTH &&
    !submitting;

  const handleSubmit = () => {
    if (!canSubmit || !definitionId) return;
    setSubmitting(true);
    setError(null);
    onSubmit(
      {
        definitionId,
        ...(name.trim() ? { name: name.trim() } : {}),
        objective: trimmedObjective,
      },
      (submitError) => {
        setSubmitting(false);
        if (submitError) setError(submitError);
      },
    );
  };

  // One stop in the tab order; the arrows move through the agents.
  const focusable = shown.filter((a) => a.enabled);
  const tabStop = focusable.find((a) => a.id === definitionId)?.id ?? focusable[0]?.id ?? null;
  const move = (fromId: string, step: number) => {
    const index = focusable.findIndex((a) => a.id === fromId);
    const next = focusable[(index + step + focusable.length) % focusable.length];
    if (!next) return;
    setDefinitionId(next.id);
    listRef.current?.querySelector<HTMLElement>(`[data-agent-id="${next.id}"]`)?.focus();
  };

  return (
    <WorkshopDialog
      isOpen
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Deploy an agent"
      description="It runs once on this project, reads its context and writes its outputs here. It spends from the AI budget."
      className="ws-deploy"
      footnote="Leave the name empty and it takes the agent's name."
      cancelLabel="Cancel"
      confirmLabel="Deploy"
      onSubmit={handleSubmit}
      confirmDisabled={!canSubmit}
      confirmLoading={submitting}
    >
      <label className="ws-share-search">
        <Search size={15} strokeWidth={1.8} aria-hidden="true" />
        <input
          type="search"
          value={find}
          onChange={(e) => setFind(e.target.value)}
          placeholder={
            agentsQuery.isLoading
              ? "Loading the agents"
              : `Find one of ${agents.length} ${agents.length === 1 ? "agent" : "agents"}`
          }
          aria-label="Find an agent"
        />
      </label>

      {agentsQuery.isError && !agentsQuery.data ? (
        <WsInlineError
          className="ws-inline-error--compact"
          title="Couldn't load the agents"
          onRetry={() => void agentsQuery.refetch()}
        />
      ) : agentsQuery.isLoading ? (
        <div className="ws-agent-list" aria-busy="true" aria-label="Loading the agents">
          {[0, 1, 2].map((i) => (
            <span key={i} className="ws-agent ws-agent--skel">
              <span className="ws-skel ws-skel--line" style={{ width: "40%" }} />
              <span className="ws-skel ws-skel--line" style={{ width: "85%" }} />
            </span>
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p className="ws-share-note">
          {agents.length === 0 ? "No agents are available to this brand yet." : "No agent matches."}
        </p>
      ) : (
        <div ref={listRef} className="ws-agent-list" role="radiogroup" aria-label="Agents">
          {shown.map((agent) => {
            const selected = agent.id === definitionId;
            return (
              <button
                key={agent.id}
                type="button"
                role="radio"
                aria-checked={selected}
                data-agent-id={agent.id}
                disabled={!agent.enabled}
                tabIndex={agent.id === tabStop ? 0 : -1}
                className={cn("ws-agent", selected && "is-selected")}
                onClick={() => setDefinitionId(agent.id)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown" || event.key === "ArrowRight") {
                    event.preventDefault();
                    move(agent.id, 1);
                  } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
                    event.preventDefault();
                    move(agent.id, -1);
                  }
                }}
              >
                <span className="ws-agent-name">
                  {agent.name}
                  {!agent.enabled ? <span className="ws-tag">Turned off</span> : null}
                  {selected ? (
                    <Check className="ms-auto size-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                  ) : null}
                </span>
                {agent.description ? <span className="ws-agent-desc">{agent.description}</span> : null}
              </button>
            );
          })}
        </div>
      )}

      <div className="ws-field">
        <label className="ws-field-label" htmlFor="ws-deploy-objective">
          What it should find out
        </label>
        <textarea
          id="ws-deploy-objective"
          className="ws-field-input ws-field-input--area"
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          placeholder="What should this agent produce?"
          rows={3}
        />
        {trimmedObjective.length > 0 && trimmedObjective.length < OBJECTIVE_MIN_LENGTH ? (
          <span className="ws-field-hint">At least {OBJECTIVE_MIN_LENGTH} characters.</span>
        ) : null}
      </div>

      <div className="ws-field">
        <label className="ws-field-label" htmlFor="ws-deploy-name">
          Name · optional
        </label>
        <input
          id="ws-deploy-name"
          className="ws-field-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={selectedAgent ? selectedAgent.name : "The agent's name"}
          maxLength={120}
        />
      </div>

      {error ? (
        <p role="alert" className="ws-field-error">
          {error}
        </p>
      ) : null}
    </WorkshopDialog>
  );
}
